import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Package,
  Hash,
  Layers,
  Sliders,
  Boxes,
  Beaker,
  Check,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { siteConfig } from "@/config/siteConfig";

const IMG_BASE = "/images/home";

const SECTIONS = [
  {
    key: "packaging",
    Icon: Package,
    title: "Packaging",
    body:
      "Printing and coding consumables for pharmaceutical primary and secondary packaging applications — including blister foil printing and pharmaceutical carton coding.",
  },
  {
    key: "coding",
    Icon: Hash,
    title: "Coding",
    body:
      "Solutions for batch information, expiry data and lot identification on production lines using HP-compatible thermal inkjet cartridges and related consumables.",
  },
  {
    key: "blister",
    Icon: Layers,
    title: "Blister Applications",
    body:
      "Specialist violet inks and supporting printing aids selected for pharmaceutical blister-pack printing on aluminium foil substrates.",
  },
  {
    key: "variable",
    Icon: Sliders,
    title: "Production Identification",
    body:
      "Consumables suited to variable-information printing across the production line — batch, lot and expiry data on porous and non-porous substrates.",
  },
  {
    key: "consumables",
    Icon: Boxes,
    title: "Consumables",
    body:
      "Complementary production-floor consumables — such as tapes, gloves, caps, masks and disposable garments — provided as a supporting supply alongside our printing and coding portfolio.",
  },
  {
    key: "evaluation",
    Icon: Beaker,
    title: "Technical Product Evaluation",
    body:
      "Every product recommendation begins with an application review, followed by a structured sample and production trial before regular commercial supply is arranged.",
  },
];

const APPLICATIONS = [
  "Blister-pack printing",
  "Batch coding",
  "Expiry-date coding",
  "Lot identification",
  "Carton coding",
  "Printing on porous substrates",
  "Printing on non-porous substrates",
  "Production-line printing consumables",
  "Cleaning and maintenance",
  "Selected production consumables",
];

const IndustryPharmaceutical = () => {
  const path = "/industries/pharmaceutical";
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Pharmaceutical Manufacturing",
    description:
      "Printing, coding and production consumables supplied for pharmaceutical manufacturing applications.",
    url: canonical,
  };

  return (
    <>
      <SEO
        rawTitle={`Pharmaceutical Manufacturing Printing & Coding Solutions | ${siteConfig.COMPANY_NAME}`}
        description="Printing, coding and production consumables for pharmaceutical manufacturing — blister printing, batch and expiry coding — supplied internationally from Dubai."
        path={path}
        extraSchema={schema}
      />
      <Helmet>
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Industries" },
            { label: "Pharmaceutical Manufacturing" },
          ]}
        />
      </div>

      {/* Hero */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-12 lg:pb-16"
        data-testid="industry-pharma-hero"
      >
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-12 lg:gap-16 items-center">
          <div>
            <p className="eyebrow">Primary Industry</p>
            <h1
              className="font-heading mt-4 text-[32px] leading-tight lg:text-[46px] lg:leading-[1.05] font-semibold text-[var(--navy)]"
              data-testid="industry-pharma-h1"
            >
              Pharmaceutical Manufacturing
            </h1>
            <p className="mt-6 text-[16.5px] leading-relaxed text-[var(--charcoal)] max-w-2xl">
              Pharmaceutical manufacturing is our primary focus. Our portfolio
              covers printing inks, coding consumables and selected production
              items supporting packaging, coding and production-line applications.
              All products are supplied subject to application testing under the
              customer&apos;s actual production conditions.
            </p>
          </div>
          <div className="image-frame">
            <picture>
              <source
                type="image/webp"
                srcSet={`${IMG_BASE}/industry-pharma-facility-800.webp 800w, ${IMG_BASE}/industry-pharma-facility.webp 1264w`}
                sizes="(min-width: 1024px) 560px, 100vw"
              />
              <img
                src={`${IMG_BASE}/industry-pharma-facility.jpg`}
                alt="Interior of a modern pharmaceutical manufacturing facility with packaging machinery"
                loading="eager"
                decoding="async"
                width="1264"
                height="848"
                className="w-full h-auto"
              />
            </picture>
          </div>
        </div>
      </section>

      {/* Six application section cards */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20 border-t border-[var(--border)]"
        data-testid="industry-pharma-sections"
      >
        <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-[var(--navy)] max-w-3xl">
          Applications we support
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {SECTIONS.map(({ key, Icon, title, body }) => (
            <article
              key={key}
              className="border border-[var(--border)] rounded-[4px] p-7 bg-white"
              data-testid={`industry-pharma-section-${key}`}
            >
              <Icon
                strokeWidth={1.5}
                style={{ width: 32, height: 32, color: "var(--pharma-blue)" }}
                aria-hidden="true"
              />
              <h3 className="font-heading mt-5 text-[19px] font-semibold text-[var(--navy)]">
                {title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[var(--slate)]">
                {body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Detailed applications list */}
      <section
        className="bg-[var(--bg-alt)]"
        data-testid="industry-pharma-apps-list"
      >
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20">
          <p className="eyebrow">Applications may include</p>
          <h2 className="font-heading mt-4 text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
            A snapshot of pharmaceutical production applications
          </h2>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl">
            {APPLICATIONS.map((a) => (
              <li key={a} className="flex items-start gap-2.5 text-[15px] text-[var(--charcoal)]">
                <Check
                  strokeWidth={1.5}
                  className="w-4 h-4 mt-1 shrink-0"
                  style={{ color: "var(--pharma-blue)" }}
                  aria-hidden="true"
                />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA band */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-20"
        data-testid="industry-pharma-cta"
      >
        <div className="border border-[var(--border)] rounded-[4px] p-8 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10">
          <div className="flex-1">
            <h2 className="font-heading text-xl lg:text-2xl font-semibold text-[var(--navy)]">
              Ready to discuss a pharmaceutical printing or coding requirement?
            </h2>
            <p className="mt-3 text-[15px] text-[var(--charcoal)] max-w-2xl">
              Explore our portfolio or send us the details of your application —
              our team will review whether an appropriate product is available
              for evaluation.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/solutions/blister-printing"
              className="btn-primary text-sm"
              data-testid="industry-pharma-cta-solutions"
            >
              Explore Pharmaceutical Solutions
            </Link>
            <Link
              to="/request-sample"
              className="btn-outline text-sm"
              data-testid="industry-pharma-cta-sample"
            >
              Request a Sample
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default IndustryPharmaceutical;
